export const MAP_BUILD_WAIT_SECONDS = 10 * 60;

export const MAP_BUILD_WAIT_MESSAGE_REGEX =
    /process map will be ready soon|cartographie de processus sera prête/i;

type MapBuildWaitAnswer = {
    question_text?: string;
    created_at?: string;
};

export function isWaitingForDocumentMapBuild(answers: MapBuildWaitAnswer[]): boolean {
    const last = answers[answers.length - 1];
    if (!last?.question_text) return false;
    return MAP_BUILD_WAIT_MESSAGE_REGEX.test(last.question_text);
}

export function findMapBuildWaitEndAnswer<T extends MapBuildWaitAnswer>(
    answers: T[],
): T | undefined {
    for (let i = answers.length - 1; i >= 0; i -= 1) {
        const text = answers[i]?.question_text ?? '';
        if (MAP_BUILD_WAIT_MESSAGE_REGEX.test(text)) {
            return answers[i];
        }
    }
    return undefined;
}

/** Temps restant (s) jusqu'au refresh, basé sur la date réelle de fin d'interview. */
export function getMapBuildWaitRemainingSeconds(
    answers: MapBuildWaitAnswer[],
    completedAt?: string | null,
): number | null {
    const endAnswer = findMapBuildWaitEndAnswer(answers);
    if (!endAnswer) return null;

    const endMs = resolveMapBuildWaitEndMs(endAnswer, completedAt);
    if (endMs === null) return MAP_BUILD_WAIT_SECONDS;

    const elapsed = Math.floor((Date.now() - endMs) / 1000);
    return Math.max(0, MAP_BUILD_WAIT_SECONDS - elapsed);
}

function resolveMapBuildWaitEndMs(
    endAnswer: MapBuildWaitAnswer,
    completedAt?: string | null,
): number | null {
    if (endAnswer.created_at) {
        const parsed = Date.parse(endAnswer.created_at);
        if (!Number.isNaN(parsed)) return parsed;
    }
    if (completedAt) {
        const parsed = Date.parse(completedAt);
        if (!Number.isNaN(parsed)) return parsed;
    }
    return null;
}

/** Clé sessionStorage pour n'auto-refresh qu'une seule fois par fin d'interview. */
export function getMapBuildAutoRefreshStorageKey(
    interviewId: string,
    answers: MapBuildWaitAnswer[],
    completedAt?: string | null,
): string | null {
    const endAnswer = findMapBuildWaitEndAnswer(answers);
    if (!endAnswer) return null;
    const endMs = resolveMapBuildWaitEndMs(endAnswer, completedAt);
    if (endMs === null) return `famla-map-build-refreshed-${interviewId}`;
    return `famla-map-build-refreshed-${interviewId}-${endMs}`;
}

export function hasMapBuildAutoRefreshDone(storageKey: string): boolean {
    try {
        return sessionStorage.getItem(storageKey) === '1';
    } catch {
        return false;
    }
}

export function markMapBuildAutoRefreshDone(storageKey: string): void {
    try {
        sessionStorage.setItem(storageKey, '1');
    } catch {
        // ignore
    }
}

export function clearMapBuildAutoRefresh(storageKey: string): void {
    try {
        sessionStorage.removeItem(storageKey);
    } catch {
        // ignore
    }
}
